import { useId } from "react";
import { useTranslation } from "react-i18next";
import { MultiTagInput, cn } from "@nexus/ui";
import type { OnboardingCreateColumn, OnboardingEditColumn } from "@nexus/contracts";
import {
  ONBOARDING_ESSENTIALS_SECTION_ID,
  ONBOARDING_FORM_SECTIONS,
  ONBOARDING_SHORT_TEXT_COLUMNS,
  isOnboardingDraftValueEmpty,
  isOnboardingJsonDraft,
  isStringListColumn,
  onboardingInputKind,
  type OnboardingDraft,
  type OnboardingDraftValue,
  type OnboardingFieldError,
  type OnboardingFieldErrors,
  type OnboardingFormSectionId,
} from "@/features/identity-hub/lib/client-onboarding";
import type { OnboardingSaveError } from "@/features/identity-hub/lib/onboarding-save-error";
import { toReadableText } from "@/features/identity-hub/lib/customer-setup";
import { FieldHelp } from "./FieldHelp";
import {
  ONBOARDING_FULL_ROW_CLASS,
  ONBOARDING_TAG_INPUT_CLASS,
  OnboardingCollapsibleSection,
  OnboardingSection,
} from "./OnboardingSection";

/** Long text gets its own row at the 68ch reading measure used across Nexus. */
const LONG_FIELD_WIDTH_CLASS = cn(ONBOARDING_FULL_ROW_CLASS, "max-w-[68ch]");

const CONTROL_CLASS =
  "w-full rounded-md border bg-background px-3 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50";

/** English labels. */
export const ONBOARDING_FIELD_LABEL_DEFAULTS: Record<OnboardingCreateColumn, string> = {
  industry: "Client industry",
  website_url: "Website",
  brand: "Brand",
  premise: "Premise",
  differentiation: "Differentiation",
  challenges: "Challenges",
  focus_area: "Focus area",
  non_focus: "Not in focus",
  additional_context_from_client: "Additional context from the client",
  target_audience: "Target audience",
  growth_target: "Growth target",
  product_to_focus_on: "Products to focus on",
  product_variance: "Product variance",
  best_sellers: "Best sellers",
  bundeling: "Bundling",
  product_description: "Product description",
  product_benefits: "Product benefits",
  tone_of_voice: "Tone of voice",
  messaging_do: "Messaging — do",
  messaging_dont: "Messaging — don't",
  things_to_avoid: "Things to avoid",
  content_strategy: "Content strategy",
  product_pricing_and_discounts: "Pricing and discounts",
  promotions: "Promotions",
  engagement: "Engagement",
  logistic_support: "Logistics support",
  known_competitors: "Known competitors",
  nemesis: "Nemesis",
  who_to_ignore: "Who to ignore",
  inspiration_industry: "Inspiration industry",
  stickrate_threshold: "Stickrate threshold",
};

/** One short hint under the label: what to enter, in plain words. */
const ONBOARDING_FIELD_HINT_DEFAULTS: Partial<Record<OnboardingCreateColumn, string>> = {
  differentiation: "What sets you apart from competitors.",
  product_variance: "The product types and categories you sell.",
  best_sellers: "Your top-selling products.",
  bundeling: "Bundles you offer, if any.",
  product_description: "What your products are.",
  product_benefits: "What customers get from your products.",
  product_pricing_and_discounts: "Your price range and the discounts you give.",
  promotions: "Promotions you run, and ones you avoid.",
  tone_of_voice: "How your brand sounds.",
  things_to_avoid: "Topics we should never mention.",
  logistic_support: "Delivery, returns and support you offer.",
  additional_context_from_client: "Anything else the system should know.",
  stickrate_threshold: "Conversions per ad, per week or month, that make an ad count as sticky.",
};

/** An example, shown in the field's help tooltip so it doesn't repeat under every field. */
const ONBOARDING_FIELD_EXAMPLE_DEFAULTS: Partial<Record<OnboardingCreateColumn, string>> = {
  differentiation: "Example: French botanical heritage combined with effective formulas and accessible pricing",
  product_variance: "Example: Skincare, haircare, body care",
  best_sellers: "Example: Hair Shine Vinegar, Monoi Pearly Oil, Anti-Age Global line",
  product_description: "Example: Botanical-powered formulas designed to deliver visible beauty results",
  product_benefits: "Example: Shiny hair, hydration, anti-aging benefits, natural ingredients",
  product_pricing_and_discounts: "Example: Mid-range pricing with frequent promotions and bundle discounts",
  promotions: "Example: Bundle discount",
  tone_of_voice: "Example: Natural, authentic, feminine and optimistic",
  logistic_support: "Example: Customer-friendly return policies and strong customer support",
  additional_context_from_client: "Example: Strong use of influencer collaborations and performance marketing",
};

/** Only where the placeholder shows the expected format rather than an example. */
const ONBOARDING_FIELD_PLACEHOLDER_DEFAULTS: Partial<Record<OnboardingCreateColumn, string>> = {
  website_url: "yourwebsite.com",
};

/** Shown fixed before the Website input, as in Retool; the stored value keeps its scheme. */
const WEBSITE_SCHEME = "https://";
const HAS_SCHEME = /^https?:\/\//i;

/** The Website input shows the address without the fixed https:// prefix. */
export function toWebsiteInput(stored: string): string {
  return stored.toLowerCase().startsWith(WEBSITE_SCHEME) ? stored.slice(WEBSITE_SCHEME.length) : stored;
}

/** Adds https:// unless the user typed a scheme themselves; blank stays blank. */
export function fromWebsiteInput(typed: string): string {
  const value = typed.trim();
  if (!value) return "";
  return HAS_SCHEME.test(value) ? value : `${WEBSITE_SCHEME}${value}`;
}

const SECTION_TITLE_DEFAULTS: Record<OnboardingFormSectionId, string> = {
  essentials: "Brand essentials",
  positioning: "Positioning",
  focus: "Focus details",
  products: "Products",
  messaging: "Messaging",
  operations: "Operations",
  competition: "Competition",
  performance: "Performance",
};

const FIELD_ERROR_DEFAULTS: Record<OnboardingFieldError, string> = {
  invalidList: "Must be a list of text values",
  invalidUrl: "Enter an http(s) URL",
  invalidNumber: "Enter a decimal number",
  invalid: "This value is too long",
};

type Translate = (key: string, defaultValue: string) => string;

/** One-line values sit in the two-column grid; everything else takes its own row. */
export function isShortOnboardingField(column: OnboardingEditColumn): boolean {
  return column === "website_url" || onboardingInputKind(column) === "numeric" || ONBOARDING_SHORT_TEXT_COLUMNS.has(column);
}

export function onboardingFieldLabel(t: Translate, column: OnboardingCreateColumn): string {
  return t(`iam.onboarding.fields.${column}`, ONBOARDING_FIELD_LABEL_DEFAULTS[column]);
}

function onboardingFieldHint(t: Translate, column: OnboardingCreateColumn): string | undefined {
  const hint = ONBOARDING_FIELD_HINT_DEFAULTS[column];
  return hint ? t(`iam.onboarding.hints.${column}`, hint) : undefined;
}

function onboardingFieldExample(t: Translate, column: OnboardingCreateColumn): string | undefined {
  const example = ONBOARDING_FIELD_EXAMPLE_DEFAULTS[column];
  return example ? t(`iam.onboarding.examples.${column}`, example) : undefined;
}

function onboardingFieldPlaceholder(t: Translate, column: OnboardingCreateColumn): string | undefined {
  const placeholder = ONBOARDING_FIELD_PLACEHOLDER_DEFAULTS[column];
  return placeholder ? t(`iam.onboarding.placeholders.${column}`, placeholder) : undefined;
}

export function onboardingFieldErrorMessage(t: Translate, error: OnboardingFieldError): string {
  return t(`iam.onboarding.errors.${error}`, FIELD_ERROR_DEFAULTS[error]);
}

interface ClientOnboardingFieldsProps {
  draft: OnboardingDraft;
  errors: OnboardingFieldErrors;
  /** A save the client-context API rejected; its reason is shown on the rejected field. */
  saveError?: OnboardingSaveError | null;
  onChange: (column: OnboardingEditColumn, value: OnboardingDraftValue) => void;
  disabled?: boolean;
  /** New organization passes `px-0`: its step body already has the side padding. */
  sectionClassName?: string;
}

/**
 * The editable onboarding columns, grouped for data entry. Client
 * Industry and the customer key are rendered by the caller — they follow
 * different rules on create and edit.
 */
export function ClientOnboardingFields({ draft, errors, saveError, onChange, disabled, sectionClassName }: ClientOnboardingFieldsProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      {ONBOARDING_FORM_SECTIONS.map((section) => {
        const title = t(`iam.onboarding.sections.${section.id}`, SECTION_TITLE_DEFAULTS[section.id]);
        const fields = section.columns.map((column) => (
          <ClientOnboardingField
            key={column}
            column={column}
            value={draft[column]}
            errorMessage={onboardingFieldErrorText(t, column, errors, saveError)}
            onChange={(value) => onChange(column, value)}
            disabled={disabled}
          />
        ));
        const testId = `onboarding-section-${section.id}`;
        if (section.id === ONBOARDING_ESSENTIALS_SECTION_ID) {
          return (
            <OnboardingSection
              key={section.id}
              className={sectionClassName}
              data-testid={testId}
              title={title}
              hint={t("iam.onboarding.essentialsHint", "Optional. The basics Brand Brain starts from.")}
            >
              {fields}
            </OnboardingSection>
          );
        }
        const filledCount = section.columns.filter((column) => !isOnboardingDraftValueEmpty(draft[column])).length;
        return (
          <OnboardingCollapsibleSection
            key={section.id}
            className={sectionClassName}
            data-testid={testId}
            title={title}
            summary={t("iam.onboarding.sectionFilled", {
              filled: filledCount,
              total: section.columns.length,
              defaultValue: "{{filled}} of {{total}} filled",
            })}
            hasError={section.columns.some((column) => onboardingFieldErrorText(t, column, errors, saveError) !== undefined)}
          >
            {fields}
          </OnboardingCollapsibleSection>
        );
      })}
    </div>
  );
}

/** A local validation error wins; otherwise the API's reason when it rejected this column. */
export function onboardingFieldErrorText(
  t: Translate,
  column: OnboardingEditColumn,
  errors: OnboardingFieldErrors,
  saveError: OnboardingSaveError | null | undefined,
): string | undefined {
  const error = errors[column];
  if (error) return onboardingFieldErrorMessage(t, error);
  return saveError?.field === column ? saveError.message : undefined;
}

interface ClientOnboardingFieldProps {
  column: OnboardingEditColumn;
  value: OnboardingDraftValue | undefined;
  errorMessage: string | undefined;
  onChange: (value: OnboardingDraftValue) => void;
  disabled?: boolean;
  /** Overrides the onboarding label, for surfaces that name fields their own way. */
  label?: string;
}

export function ClientOnboardingField({
  column,
  value,
  errorMessage: error,
  onChange,
  disabled,
  label: labelOverride,
}: ClientOnboardingFieldProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const errorId = useId();
  const kind = onboardingInputKind(column);
  const label = labelOverride ?? onboardingFieldLabel(t, column);
  const borderClass = error ? "border-destructive focus:border-destructive" : "border-input-border";
  const describedBy = error ? errorId : undefined;
  const text = typeof value === "string" ? value : "";
  const hint = onboardingFieldHint(t, column);
  const placeholder = onboardingFieldPlaceholder(t, column);
  const example = onboardingFieldExample(t, column);
  /** A stored text value in a generic JSON column stays text; it is never forced into a list. */
  const isTextInJsonColumn = kind === "json" && typeof value === "string";

  return (
    // Short values sit in the two-column grid; long text takes its own row at a readable measure.
    <div className={cn("flex w-full min-w-0 flex-col gap-1.5", !isShortOnboardingField(column) && LONG_FIELD_WIDTH_CLASS)}>
      <div className="flex items-center gap-1.5">
        <label htmlFor={inputId} className="text-xs font-medium text-foreground">
          {label}
        </label>
        {example && <FieldHelp label={label}>{example}</FieldHelp>}
      </div>
      {hint && <p className="-mt-0.5 text-[11px] leading-snug text-muted-foreground">{hint}</p>}
      {kind === "json" && isOnboardingJsonDraft(value) && <StoredValueNotList json={value.json} />}
      {kind === "json" && !isTextInJsonColumn ? (
        <MultiTagInput
          className={ONBOARDING_TAG_INPUT_CLASS}
          id={inputId}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          disableDropdown
          disabled={disabled}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          placeholder={
            placeholder ??
            (isStringListColumn(column)
              ? t("iam.onboarding.listPlaceholder", "Type a product and press Enter")
              : t("iam.onboarding.valueListPlaceholder", "Type a value and press Enter"))
          }
          addFreeTextLabel={(item) => t("iam.onboarding.listAdd", { item, defaultValue: "Add {{item}}" })}
        />
      ) : column === "website_url" ? (
        <div
          className={cn(
            "flex h-9 w-full overflow-hidden rounded-md border bg-background focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40",
            borderClass,
          )}
        >
          <span className="flex items-center border-r border-input-border bg-muted/40 px-3 text-[13px] font-medium text-muted-foreground">
            {WEBSITE_SCHEME}
          </span>
          <input
            id={inputId}
            type="text"
            inputMode="url"
            value={toWebsiteInput(text)}
            onChange={(e) => onChange(fromWebsiteInput(e.target.value))}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className="min-w-0 flex-1 bg-transparent px-3 text-[13px] text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      ) : kind === "numeric" || ONBOARDING_SHORT_TEXT_COLUMNS.has(column) ? (
        <input
          id={inputId}
          type="text"
          inputMode={kind === "numeric" ? "decimal" : undefined}
          value={text}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(CONTROL_CLASS, borderClass, "h-9", kind === "numeric" && "max-w-[200px]")}
        />
      ) : (
        <textarea
          id={inputId}
          value={text}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={2}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          // Grows with its text so long values are never hidden behind a scrollbar; capped so one field can't take the page.
          className={cn(CONTROL_CLASS, borderClass, "min-h-14 max-h-80 resize-y py-2 leading-relaxed field-sizing-content")}
        />
      )}
      {error && (
        <p id={errorId} className="text-[11px] text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/** A list column whose stored value is not a list of text: shown read-only, and only replaced if the user edits the tags. */
function StoredValueNotList({ json }: { json: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1" data-testid="onboarding-stored-not-list">
      <p className="text-[11px] text-muted-foreground">
        {t(
          "iam.onboarding.storedNotList",
          "The stored value is not a list of text values. Adding items here replaces it.",
        )}
      </p>
      <p
        className="max-h-32 overflow-auto rounded-md border border-border-subtle p-2 text-[11px] text-muted-foreground"
        data-testid="onboarding-stored-value"
      >
        {toReadableText(JSON.parse(json))}
      </p>
    </div>
  );
}
